import { useSWRConfig } from 'swr';

/**
 * SWR Cache Manager - Utilities for invalidating SWR caches
 * Handles cache invalidation patterns for different API endpoints
 */

export function useSWRCacheManager() {
  const { cache, mutate } = useSWRConfig();

  /**
   * Invalidate all cache keys matching a pattern
   */
  const invalidateByPattern = (pattern: RegExp) => {
    if (!cache) return;

    const allKeys = Array.from((cache as any).keys?.() ?? []);
    allKeys.forEach((key: string) => {
      if (pattern.test(key)) {
        mutate(key);
      }
    });
  };

  /**
   * Invalidate all movie-related caches after a movie update
   * Clears all endpoints that might contain the updated movie
   */
  const invalidateMovieCaches = (movieId?: string | number) => {
    if (!cache) return;

    const allKeys = Array.from((cache as any).keys?.() ?? []);

    allKeys.forEach((key: string) => {
      // Always invalidate these endpoints as they depend on movie data
      if (
        /^\/api\/upcoming\//.test(key) || // Upcoming movies
        /^\/api\/search\//.test(key) || // Search results
        /^\/api\/u\//.test(key) || // User ratings/watchlist
        /^\/api\/activity\//.test(key) || // Activity feed
        /^\/api\/p\//.test(key) // Person filmography
      ) {
        mutate(key);
      }

      // If specific movieId provided, also invalidate that movie's detailed view
      if (movieId && new RegExp(`/movieById/${movieId}`).test(key)) {
        mutate(key);
      }
    });
  };

  /**
   * Force revalidate a specific key immediately
   */
  const revalidate = (key: string) => {
    mutate(key);
  };

  /**
   * Clear all SWR caches (nuclear option)
   */
  const clearAll = () => {
    if (!cache) return;
    const allKeys = Array.from((cache as any).keys?.() ?? []);
    allKeys.forEach((key: string) => {
      mutate(key);
    });
  };

  return {
    invalidateByPattern,
    invalidateMovieCaches,
    revalidate,
    clearAll,
  };
}
